import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { fetchWithAuth } from '../../lib/api';
import {
  Search, Filter, RefreshCw, Download, ChevronLeft, ChevronRight,
  Camera, Wand2, Video, UserPlus, Building2, Package, AlertTriangle, RotateCcw,
  ArrowUpRight, ArrowDownRight, CreditCard, Activity, ScrollText
} from 'lucide-react';

const TYPE_META = {
  scan_generated:           { icon: Camera, label: '4K Üretildi', color: 'bg-amber-100 text-amber-700' },
  ultrasound_4k_started:    { icon: Camera, label: '4K Başlatıldı', color: 'bg-amber-100 text-amber-700' },
  ultrasound_4k_completed:  { icon: Camera, label: '4K Tamamlandı', color: 'bg-emerald-100 text-emerald-700' },
  scan_failed:              { icon: AlertTriangle, label: 'Başarısız', color: 'bg-rose-100 text-rose-700' },
  ai_face_generated:        { icon: Wand2, label: 'AI Bebek Yüzü', color: 'bg-pink-100 text-pink-700' },
  video_generation_started: { icon: Video, label: '4D Video', color: 'bg-indigo-100 text-indigo-700' },
  user_created:             { icon: UserPlus, label: 'Doktor Kayıt', color: 'bg-emerald-100 text-emerald-700' },
  mom_registered:           { icon: UserPlus, label: 'Anne Kayıt', color: 'bg-fuchsia-100 text-fuchsia-700' },
  clinic_created:           { icon: Building2, label: 'Klinik Açıldı', color: 'bg-blue-100 text-blue-700' },
  package_assigned:         { icon: Package, label: 'Paket Atandı', color: 'bg-violet-100 text-violet-700' },
  package_purchased:        { icon: Package, label: 'Paket Satın Alındı', color: 'bg-violet-100 text-violet-700' },
  credit_granted:           { icon: ArrowUpRight, label: 'Kredi Verildi', color: 'bg-emerald-100 text-emerald-700' },
  credit_spent:             { icon: ArrowDownRight, label: 'Kredi Kullanıldı', color: 'bg-rose-100 text-rose-700' },
  credit_refunded:          { icon: RotateCcw, label: 'Kredi İade', color: 'bg-sky-100 text-sky-700' },
  promo_code_generated:     { icon: CreditCard, label: 'Promo Üretildi', color: 'bg-violet-100 text-violet-700' },
  promo_code_used:          { icon: CreditCard, label: 'Promo Kullanıldı', color: 'bg-violet-100 text-violet-700' },
  limit_exceeded:           { icon: AlertTriangle, label: 'Limit Aşımı', color: 'bg-rose-100 text-rose-700' },
};

const ACTION_OPTIONS = [
  { value: '', label: 'Tüm Olaylar' },
  ...Object.entries(TYPE_META).map(([v, m]) => ({ value: v, label: m.label })),
];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function describeDetails(item) {
  const d = item.details || {};
  const parts = [];
  if (d.amount) parts.push(`${d.amount} kredi`);
  if (d.feature_key) parts.push(`özellik: ${d.feature_key}`);
  if (d.code) parts.push(`kod: ${d.code}`);
  if (d.credits_added) parts.push(`+${d.credits_added} kredi`);
  if (d.engine) parts.push(`motor: ${d.engine}`);
  if (d.reason) parts.push(`sebep: ${d.reason}`);
  if (d.count) parts.push(`${d.count} adet`);
  return parts.join(' · ') || '—';
}

const ROLE_LABEL = { doctor: 'Doktor', mother: 'Anne', super_admin: 'Süper Admin', system: 'Sistem' };

export default function AuditLogPage() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const limit = 25;
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actionType, setActionType] = useState('');
  const [patientName, setPatientName] = useState('');
  const [quickFilter, setQuickFilter] = useState(() => {
    if (searchParams.get('credit_only') === 'true') return 'credit';
    if (searchParams.get('campaign_only') === 'true') return 'campaign';
    if (searchParams.get('failed_only') === 'true') return 'failed';
    return '';
  });

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', limit);
      qs.set('offset', offset);
      if (dateFrom) qs.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) qs.set('date_to', new Date(dateTo + 'T23:59:59').toISOString());
      if (actionType) qs.set('action_type', actionType);
      if (patientName) qs.set('patient_name', patientName);
      if (quickFilter === 'credit') qs.set('credit_only', 'true');
      if (quickFilter === 'campaign') qs.set('campaign_only', 'true');
      if (quickFilter === 'failed') qs.set('failed_only', 'true');
      const data = await fetchWithAuth(`/superadmin/activity-feed/audit-log?${qs.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [offset, dateFrom, dateTo, actionType, patientName, quickFilter]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const resetFilters = () => {
    setDateFrom(''); setDateTo(''); setActionType(''); setPatientName(''); setQuickFilter(''); setOffset(0);
  };

  const exportCsv = () => {
    const rows = [
      ['Tarih', 'Olay', 'Aktör', 'Rol', 'Klinik', 'Hedef', 'Detay', 'Durum'],
      ...items.map((it) => [
        fmtDate(it.created_at), TYPE_META[it.type]?.label || it.type,
        it.actor_name || '', ROLE_LABEL[it.actor_role] || it.actor_role || '',
        it.clinic_name || '', it.target || '', describeDetails(it), it.status || '',
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const QuickPill = ({ value, label }) => (
    <button
      onClick={() => { setQuickFilter(value === quickFilter ? '' : value); setOffset(0); }}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        quickFilter === value ? 'bg-[#5A67D8] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
      data-testid={`quick-${value || 'all'}`}
    >{label}</button>
  );

  return (
    <DashboardLayout title="İşlem Geçmişi" subtitle="Sistemde gerçekleşen tüm önemli olayların kaydı">
      <div className="space-y-4" data-testid="audit-log-page">
        {/* Filters card */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Filtreler</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={resetFilters} data-testid="reset-filters">
                  Sıfırla
                </Button>
                <Button variant="outline" size="sm" onClick={fetchFeed} data-testid="refresh-audit">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Yenile
                </Button>
                <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv">
                  <Download className="w-3.5 h-3.5 mr-1" /> CSV
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <QuickPill value="" label="Tümü" />
              <QuickPill value="credit" label="Sadece Kredi" />
              <QuickPill value="campaign" label="Sadece Kampanya" />
              <QuickPill value="failed" label="Sadece Başarısız" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Başlangıç Tarihi</label>
                <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }} data-testid="filter-date-from" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Bitiş Tarihi</label>
                <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setOffset(0); }} data-testid="filter-date-to" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Olay Türü</label>
                <select
                  value={actionType}
                  onChange={(e) => { setActionType(e.target.value); setOffset(0); }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="filter-action-type"
                >
                  {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Hasta Adı</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={patientName}
                    onChange={(e) => { setPatientName(e.target.value); setOffset(0); }}
                    placeholder="örn: Ayşe"
                    className="pl-9"
                    data-testid="filter-patient-name"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results summary */}
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span data-testid="audit-total">{loading ? 'Yükleniyor...' : `${total} kayıt bulundu`}</span>
          <span className="text-xs text-slate-400">Gösterilen: {Math.min(offset + 1, total)}–{Math.min(offset + items.length, total)}</span>
        </div>

        {/* Table */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                <tr>
                  <th className="text-left py-3 px-4 font-medium">Tarih</th>
                  <th className="text-left py-3 px-4 font-medium">Olay</th>
                  <th className="text-left py-3 px-4 font-medium">Aktör</th>
                  <th className="text-left py-3 px-4 font-medium">Rol</th>
                  <th className="text-left py-3 px-4 font-medium">Klinik</th>
                  <th className="text-left py-3 px-4 font-medium">Hedef</th>
                  <th className="text-left py-3 px-4 font-medium">Detay</th>
                  <th className="text-left py-3 px-4 font-medium">Durum</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="py-10 text-center text-slate-400">Yükleniyor...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan="8" className="py-10 text-center text-slate-400" data-testid="audit-empty">Filtrenize uyan kayıt yok.</td></tr>
                ) : (
                  items.map((it) => {
                    const meta = TYPE_META[it.type] || { icon: Activity, label: it.type, color: 'bg-slate-100 text-slate-700' };
                    const Icon = meta.icon;
                    return (
                      <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`audit-row-${it.type}`}>
                        <td className="py-3 px-4 text-slate-600 whitespace-nowrap">{fmtDate(it.created_at)}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${meta.color}`}>
                            <Icon className="w-3 h-3" />
                            {meta.label}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-700 font-medium">{it.actor_name || '—'}</td>
                        <td className="py-3 px-4 text-slate-500 text-xs">{ROLE_LABEL[it.actor_role] || it.actor_role || '—'}</td>
                        <td className="py-3 px-4 text-slate-600">{it.clinic_name || '—'}</td>
                        <td className="py-3 px-4 text-slate-600">{it.patient_short || it.target || '—'}</td>
                        <td className="py-3 px-4 text-slate-500 text-xs">{describeDetails(it)}</td>
                        <td className="py-3 px-4">
                          {it.status === 'failed' ? (
                            <span className="px-2 py-0.5 rounded text-xs bg-rose-50 text-rose-600">başarısız</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-xs bg-emerald-50 text-emerald-600">tamam</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline" size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              data-testid="pagination-prev"
            >
              <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Önceki
            </Button>
            <span className="text-xs text-slate-500">Sayfa {Math.floor(offset / limit) + 1} / {Math.ceil(total / limit)}</span>
            <Button
              variant="outline" size="sm"
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
              data-testid="pagination-next"
            >
              Sonraki <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
